/**
 * Delete Class Dialog
 * Confirmation dialog for deleting a class with safety checks
 *
 * Safety Rules:
 * - Block deletion if labs are provisioned (must terminate first)
 * - Warn about cascade deletion of enrollments
 * - Require checkbox confirmation
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { AlertCircle, AlertTriangle, Trash2 } from 'lucide-react';
import type { Class } from '@cloak-next/shared';

interface DeleteClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classData: Class;
  onDeleteClass: (className: string) => Promise<void>;
  onTerminateLabs?: () => Promise<void>;
}

export function DeleteClassDialog({
  open,
  onOpenChange,
  classData,
  onDeleteClass,
  onTerminateLabs,
}: DeleteClassDialogProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmed(false);
      setError(null);
    }
  }, [open]);

  const hasProvisionedLabs = classData.labsProvisioned;
  const hasEnrollments = classData.currentStudents > 0;

  const handleDelete = async () => {
    if (!confirmed || hasProvisionedLabs) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await onDeleteClass(classData.className);
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete class');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTerminateLabs = async () => {
    if (!onTerminateLabs) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onTerminateLabs();
      // Dialog stays open - user needs to confirm deletion after labs are terminated
    } catch (err: any) {
      setError(err.message || 'Failed to terminate labs');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Class
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. The class and all associated data will be permanently deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Labs Provisioned Warning - Blocking */}
          {hasProvisionedLabs && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Cannot Delete</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  This class has provisioned lab instances. You must terminate all labs before
                  deleting the class.
                </p>
                {onTerminateLabs && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTerminateLabs}
                    disabled={isDeleting}
                    className="mt-2"
                  >
                    Terminate Labs First
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Enrollments Warning - Non-blocking */}
          {!hasProvisionedLabs && hasEnrollments && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning: Enrolled Students</AlertTitle>
              <AlertDescription>
                <p>
                  This class has <strong>{classData.currentStudents} enrolled student{classData.currentStudents !== 1 ? 's' : ''}</strong>.
                  All enrollment records will be deleted.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Note: Student accounts will NOT be deleted from the system.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Class Info Summary */}
          <div className="rounded-lg border p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Class:</span>
              <span className="font-medium">{classData.classTitle || classData.className}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status:</span>
              <span className="font-medium">{classData.status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Enrolled Students:</span>
              <span className="font-medium">{classData.currentStudents}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Labs Provisioned:</span>
              <span className="font-medium">{classData.labsProvisioned ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {/* Confirmation Checkbox */}
          {!hasProvisionedLabs && (
            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="confirm-delete"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                disabled={isDeleting}
              />
              <Label
                htmlFor="confirm-delete"
                className="text-sm font-normal leading-tight cursor-pointer"
              >
                I understand that this action is permanent and all class data
                {hasEnrollments ? ', including enrollment records,' : ''} will be deleted.
              </Label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!confirmed || hasProvisionedLabs || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Class'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
